import { motion } from "framer-motion";
import {
  ShieldCheck,
  Leaf,
  Bike,
  Heart,
  Sparkles,
  ArrowRight,
  Check,
  Store,
  MapPin,
  Users,
} from "lucide-react";
import { CtaDialog } from "@/components/common/CtaDialog";
import { SectionReveal } from "@/components/common/SectionReveal";
import { KITCHENS } from "@/constants/kitchens";

const PRIMARY_BTN =
  "h-11 bg-halalgo-green-600 px-5 text-white shadow-lift-sm hover:bg-halalgo-green-700 active:scale-[0.98]";
const KRAFT_BTN =
  "h-11 bg-white px-5 text-halalgo-ink border border-halalgo-kraft-300 shadow-inset-kraft hover:bg-halalgo-kraft-50 active:scale-[0.98]";
const WHITE_BTN =
  "h-11 bg-white px-5 text-halalgo-ink shadow-lift-sm hover:bg-white/90 active:scale-[0.98]";

const VALUES = [
  {
    icon: ShieldCheck,
    accent: Leaf,
    title: "100% Halal",
    body: "Safe, Clean, Trusted.",
  },
  {
    icon: Bike,
    title: "Fast & Reliable Delivery",
    body: "We deliver goodness — fast, safe, and halal.",
  },
  {
    icon: Heart,
    title: "Support Local",
    body: "Eat Halal, Live Easy.",
  },
];

// ---- Reusable CTA dialog configs (submissions saved to backend) ----
const waitlistCta = (testId, source, extra = {}) => ({
  label: "Join the Waitlist",
  icon: Sparkles,
  formType: "waitlist",
  source,
  title: "Join the Waitlist",
  description:
    "Be the first to order when Halal GO launches in your city. Drop your email and we'll notify you the moment we go live.",
  waitlistPrefix: `${testId}-wl`,
  waitlistButtonLabel: "Notify me",
  testId,
  ...extra,
});

const merchantCta = (testId, source, extra = {}) => ({
  label: "Become a Partner",
  icon: Store,
  accentIcon: Store,
  formType: "merchant",
  source,
  title: "Become a Partner",
  description:
    "Join our Trusted Halal Partners program and grow your kitchen with Halal GO. Share your details and our partnerships team will reach out.",
  waitlistPrefix: `${testId}-wl`,
  waitlistButtonLabel: "Submit application",
  testId,
  ...extra,
});

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ============================== HERO ============================== */}
      <section className="hero-wash">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <div className="flex items-center gap-3">
              <span
                lang="ar"
                dir="rtl"
                className="text-2xl font-semibold leading-none text-halalgo-green-700"
              >
                حلال
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-halalgo-green-200 bg-halalgo-green-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-halalgo-green-800">
                <Sparkles className="h-3.5 w-3.5" /> Launching soon in the Philippines
              </span>
            </div>

            <h1 className="mt-5 text-5xl font-bold tracking-tight text-halalgo-ink sm:text-6xl lg:text-7xl">
              Halal Made <span className="text-halalgo-green-700">Easy.</span>
            </h1>

            <p className="mt-5 max-w-xl text-lg leading-relaxed text-halalgo-ink/70">
              Your trusted Halal food delivery. From trusted kitchens to your door.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <CtaDialog {...waitlistCta("hero-join-waitlist", "home-hero", { buttonClassName: PRIMARY_BTN })} />
              <CtaDialog {...merchantCta("hero-become-partner", "home-hero", { buttonClassName: KRAFT_BTN })} />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-halalgo-ink/70">
              {["Safe", "Clean", "Trusted"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-halalgo-green-600" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Hero visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-halalgo-kraft-200 bg-white shadow-lift-md">
              <img
                src="/showcase/hero-spread.png"
                alt="A spread of freshly prepared halal dishes"
                className="h-[320px] w-full object-cover sm:h-[440px]"
                loading="eager"
              />
            </div>

            <div className="absolute -left-3 top-6 flex items-center gap-2 rounded-xl border border-halalgo-green-200 bg-white/95 px-3 py-2 shadow-lift-sm backdrop-blur">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-halalgo-green-600 text-white">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-bold text-halalgo-ink">100% Halal</p>
                <p className="text-[11px] text-halalgo-ink/60">Safe • Clean • Trusted</p>
              </div>
            </div>

            <div className="absolute -bottom-3 right-6 flex items-center gap-2 rounded-xl border border-halalgo-kraft-200 bg-white/95 px-3 py-2 shadow-lift-sm backdrop-blur">
              <Leaf className="h-5 w-5 text-halalgo-kraft-700" />
              <p className="text-sm font-semibold text-halalgo-ink">Eco Packaging</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ======================= VALUE PROPOSITION ======================= */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-6 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <SectionReveal
              key={v.title}
              delay={i * 0.08}
              className="relative overflow-hidden rounded-xl border border-halalgo-kraft-100 bg-white p-6 shadow-lift-sm"
            >
              <span className="absolute inset-x-0 top-0 h-1 bg-halalgo-green-600" />
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-halalgo-green-600 text-white">
                <v.icon className="h-6 w-6" />
                {v.accent ? (
                  <v.accent className="ml-0.5 h-4 w-4 text-halalgo-green-100" />
                ) : null}
              </div>
              <h3 className="mt-5 text-lg font-bold uppercase tracking-wide text-halalgo-ink">
                {v.title}
              </h3>
              <p className="mt-1.5 text-halalgo-ink/70">{v.body}</p>
            </SectionReveal>
          ))}
        </div>
      </section>

      {/* ==================== TRUSTED KITCHENS SHOWCASE ==================== */}
      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <SectionReveal className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-halalgo-kraft-700">
              Restaurant showcase
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-halalgo-ink sm:text-4xl">
              Our Soon-to-Be Trusted Kitchens
            </h2>
            <p className="mt-3 text-halalgo-ink/70">
              A taste of what's coming — verified halal kitchens preparing to join
              Halal GO across the Philippines.
            </p>
          </div>
          <span className="hidden items-center gap-1 text-sm font-medium text-halalgo-green-800 sm:inline-flex">
            Scroll to explore <ArrowRight className="h-4 w-4" />
          </span>
        </SectionReveal>

        <div
          className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4"
          data-testid="kitchens-showcase"
        >
          {KITCHENS.map((k) => (
            <article
              key={k.name}
              data-testid="kitchen-card"
              className="group relative w-72 shrink-0 snap-start overflow-hidden rounded-xl border border-halalgo-kraft-100 bg-white shadow-lift-sm transition-shadow hover:shadow-lift-md"
            >
              <div className="relative overflow-hidden">
                <img
                  src={k.img}
                  alt={`${k.name} — ${k.cuisine}`}
                  className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute left-3 top-3 rounded-md bg-white/90 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-halalgo-green-800 backdrop-blur">
                  Coming soon
                </span>
                <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-halalgo-green-600/95 px-2 py-0.5 text-[11px] font-medium text-white">
                  <ShieldCheck className="h-3 w-3" /> Halal
                </span>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-halalgo-ink">{k.name}</h3>
                  <span className="shrink-0 rounded bg-halalgo-kraft-50 px-1.5 py-0.5 text-[10px] font-medium text-halalgo-kraft-800">
                    {k.tag}
                  </span>
                </div>
                <p className="mt-1 text-sm text-halalgo-ink/70">{k.cuisine}</p>
                <p className="mt-2 flex items-center gap-1 text-xs text-halalgo-ink/55">
                  <MapPin className="h-3.5 w-3.5" /> {k.area}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ===================== COMMUNITY PILLARS ===================== */}
      <section className="mx-auto max-w-6xl space-y-6 px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <SectionReveal className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-halalgo-kraft-700">
            Built for the community
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-halalgo-ink sm:text-4xl">
            One platform, three ways to belong
          </h2>
        </SectionReveal>

        {/* Section A — Customers */}
        <SectionReveal className="overflow-hidden rounded-2xl border border-halalgo-green-200 bg-halalgo-green-50">
          <div className="grid items-center gap-6 p-8 sm:p-10 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-halalgo-green-800">
                <Users className="h-3.5 w-3.5" /> For food lovers
              </div>
              <h3 className="mt-4 text-2xl font-bold tracking-tight text-halalgo-ink sm:text-3xl">
                Craving your favorites?
              </h3>
              <p className="mt-3 max-w-md text-halalgo-ink/75">
                Get them delivered with absolute peace of mind — 100% halal, from
                trusted kitchens straight to your door.
              </p>
              <div className="mt-6">
                <CtaDialog {...waitlistCta("pillar-customer-cta", "home-pillar-customer", { buttonClassName: PRIMARY_BTN })} />
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-halalgo-green-200 shadow-lift-sm">
              <img
                src="/showcase/manila-halal-hub.png"
                alt="Halal dish delivered to customers"
                className="h-56 w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </SectionReveal>

        {/* Section B — Restaurants */}
        <SectionReveal className="overflow-hidden rounded-2xl border border-halalgo-kraft-200 bg-halalgo-kraft-100">
          <div className="grid items-center gap-6 p-8 sm:p-10 lg:grid-cols-2">
            <div className="order-2 overflow-hidden rounded-xl border border-halalgo-kraft-200 shadow-lift-sm lg:order-1">
              <img
                src="/showcase/sultans-grill.png"
                alt="A partner kitchen's halal grill platter"
                className="h-56 w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-halalgo-kraft-800">
                <Store className="h-3.5 w-3.5" /> For restaurants
              </div>
              <h3 className="mt-4 text-2xl font-bold tracking-tight text-halalgo-ink sm:text-3xl">
                Grow your business
              </h3>
              <p className="mt-3 max-w-md text-halalgo-ink/75">
                Join our Trusted Halal Partners program and reach more customers who
                are looking for exactly what you serve.
              </p>
              <div className="mt-6">
                <CtaDialog
                  {...merchantCta("pillar-partner-cta", "home-pillar-restaurant", {
                    label: "Partner with Us",
                    buttonClassName: PRIMARY_BTN,
                  })}
                />
              </div>
            </div>
          </div>
        </SectionReveal>

        {/* Section C — Riders */}
        <SectionReveal className="overflow-hidden rounded-2xl bg-halalgo-ink text-white">
          <div className="grid items-center gap-6 p-8 sm:p-10 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                <Bike className="h-3.5 w-3.5" /> For riders
              </div>
              <h3 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">
                Ride with pride
              </h3>
              <p className="mt-3 max-w-md text-white/75">
                Join a fleet that respects your values. Flexible hours, fair pay, and
                a community that has your back.
              </p>
              <div className="mt-6">
                <CtaDialog
                  label="Drive for Us"
                  icon={Bike}
                  accentIcon={Bike}
                  formType="rider"
                  source="home-pillar-rider"
                  title="Ride with Halal GO"
                  description="Be part of our founding rider fleet. Leave your details and we'll reach out with the next steps before launch."
                  waitlistPrefix="pillar-rider-cta-wl"
                  waitlistButtonLabel="Count me in"
                  testId="pillar-rider-cta"
                  buttonClassName={WHITE_BTN}
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-white/10 shadow-lift-sm">
              <img
                src="https://images.unsplash.com/photo-1702893751953-ec342c3d7e78?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
                alt="A Halal GO delivery rider on a scooter"
                className="h-56 w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </SectionReveal>
      </section>
    </div>
  );
}
